import { jsx } from "react/jsx-runtime";
import { TextField } from "@wix/wix-ui-tpa/cssVars";
import { useRef, useState, useMemo, useEffect, useCallback } from "react";
import { useConfig } from "../../../../contexts/config-context.js";
import { useSubmitSuccessEvent, useFocusFieldEvent } from "../../../../contexts/events-context.js";
import { useFocus } from "../../../../hooks/use-focus.js";
import { Flag } from "../flag.js";
import { parseCountryCodeFromPhoneNumber } from "../phone-input-utils.js";
import { useCountries, filterCountries, searchCountries } from "../use-countries.js";
import { classes as classes$1 } from "./country-option.st.css";
import { DrawerWithSearch } from "./drawer-with-search.js";
import { DropdownWithSearch } from "./dropdown-with-search.js";
import { st, classes } from "./phone-input-wut.st.css";
const PhoneInputWUT = ({
  fieldId,
  inputId,
  value,
  allowedCountryCodes,
  defaultCountry,
  showFlag,
  placeholder,
  disabled,
  required,
  hasError,
  ariaDescribedBy,
  ariaInvalid,
  className,
  theme,
  readOnly,
  onChange,
  onFocus,
  onBlur,
  ariaLabel
}) => {
  const { isMobile, isRTL: rtl } = useConfig();
  const phoneInputRef = useRef(null);
  const [searchPhrase, setSearchPhrase] = useState("");
  const countryCodeFromPhoneNumber = useMemo(
    () => parseCountryCodeFromPhoneNumber(value),
    [value]
  );
  const [selectedCountryCode, setSelectedCountryCode] = useState(defaultCountry ?? countryCodeFromPhoneNumber);
  const allCountries = useCountries();
  const allowedCountries = useMemo(
    () => filterCountries(allCountries, allowedCountryCodes),
    [allCountries, allowedCountryCodes]
  );
  const countries = useMemo(
    () => searchCountries(allowedCountries, searchPhrase),
    [allowedCountries, searchPhrase]
  );
  const isValidCountryCode = useMemo(
    () => allowedCountries.find(
      (country) => country.shortCountryCode === selectedCountryCode
    ),
    [allowedCountries, selectedCountryCode]
  );
  const validCountryCode = isValidCountryCode ? selectedCountryCode : void 0;
  useSubmitSuccessEvent(() => {
    setSelectedCountryCode(defaultCountry);
  });
  useEffect(() => {
    setSelectedCountryCode(defaultCountry ?? countryCodeFromPhoneNumber);
  }, [countryCodeFromPhoneNumber, defaultCountry]);
  const handleKeydown = useCallback(
    (event) => {
      switch (event.key) {
        case "Tab":
          phoneInputRef.current?.focus();
          break;
      }
    },
    []
  );
  const handleSelect = useCallback(
    (countryCode) => {
      setSelectedCountryCode(countryCode);
      onChange(value, countryCode);
      phoneInputRef.current?.focus();
    },
    [value, onChange]
  );
  const handleChange = useCallback(
    (event) => {
      const countryCodeByPhoneNumber = parseCountryCodeFromPhoneNumber(
        event.target.value
      );
      if (countryCodeByPhoneNumber) {
        setSelectedCountryCode(countryCodeByPhoneNumber);
        onChange(event.target.value, countryCodeByPhoneNumber);
        return;
      }
      onChange(event.target.value, selectedCountryCode);
    },
    [onChange, selectedCountryCode]
  );
  const { containerRef, handleBlur, handleFocus } = useFocus({
    onBlur,
    onFocus
  });
  useFocusFieldEvent(() => {
    phoneInputRef.current?.focus({ preventScroll: true });
  }, fieldId);
  const renderPrefix = () => {
    if (allowedCountries.length === 1) {
      return showFlag ? /* @__PURE__ */ jsx("div", { className: classes$1.flag, children: /* @__PURE__ */ jsx(Flag, { longCountryCode: countries[0].longCountryCode }) }) : null;
    }
    return isMobile ? /* @__PURE__ */ jsx(
      DrawerWithSearch,
      {
        readOnly,
        className,
        theme,
        countries,
        countryCode: validCountryCode,
        onSelect: handleSelect,
        searchPhrase,
        onSearch: setSearchPhrase,
        onKeyDown: handleKeydown,
        ariaLabel
      }
    ) : /* @__PURE__ */ jsx(
      DropdownWithSearch,
      {
        readOnly,
        disabled,
        className,
        theme,
        countries,
        countryCode: validCountryCode,
        onSelect: handleSelect,
        searchPhrase,
        onSearch: setSearchPhrase,
        onKeyDown: handleKeydown,
        ariaLabel
      }
    );
  };
  return /* @__PURE__ */ jsx("div", { ref: containerRef, onBlur: handleBlur, onFocus: handleFocus, children: /* @__PURE__ */ jsx(
    TextField,
    {
      ref: phoneInputRef,
      id: inputId,
      type: "phone",
      inputMode: "tel",
      error: hasError,
      newErrorMessage: true,
      value: typeof value === "string" ? value : "",
      className: st(classes.input, { rtl }, className),
      required,
      disabled,
      placeholder,
      theme,
      "aria-describedby": ariaDescribedBy,
      "aria-invalid": ariaInvalid,
      "aria-label": ariaLabel,
      onChange: handleChange,
      prefix: renderPrefix(),
      readOnlyCustom: readOnly
    }
  ) });
};
export {
  PhoneInputWUT
};
//# sourceMappingURL=phone-input-wut.js.map
